import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import React from 'react';

import { z } from 'zod';

import { VoiceoverRegistry } from '../registries/VoiceoverRegistry';
import type { CustomRendererComponentProps, ToolExecutionContext } from '../types';
import { Tool } from './Tool';

import { AudioSearchSourceEnum } from 'js/src/client';
import { ApiService } from 'js/src/client/services.gen';

const RESULT_COUNT = 10;

export const searchUploadedVoiceoversSchema = z.object({
    query: z
        .string()
        .optional()
        .describe(
            "Optional query to filter uploaded voiceovers by name or tags. Must be in English. Omit it to list the user's most recent uploaded voiceovers.",
        ),
});
export type SearchUploadedVoiceoversArgs = z.infer<typeof searchUploadedVoiceoversSchema>;

interface VoiceoverRepresentation {
    id: string;
    type: string;
    title: string;
    tags: string;
    duration: number;
    has_voiceover: boolean;
}

export class SearchUploadedVoiceoversTool extends Tool<typeof searchUploadedVoiceoversSchema> {
    protected override readonly schema = searchUploadedVoiceoversSchema;
    protected override readonly toolName = TOOL_NAMES.searchUploadedVoiceovers;
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    protected override readonly toolDescription = `List or search the user's previously-uploaded voiceover audio files (human recordings). Use this only when the user references their own uploaded voiceovers. Each result includes an id that can be passed as audio_id to ${TOOL_NAMES.addRecordedVoiceoverSceneGroup}.`;

    public override customRenderer = SearchUploadedVoiceoversResultRenderer;

    public override getToolDisplayName(): string {
        return 'Searching uploaded voiceovers';
    }

    public get statusIcon(): string {
        return 'record_voice_over';
    }

    public async execute(args: SearchUploadedVoiceoversArgs, _context: ToolExecutionContext) {
        const query = args.query?.trim() ?? '';

        try {
            const response = await ApiService.audioRetrieve({
                query: {
                    q: query,
                    search_source: AudioSearchSourceEnum.VOICEOVER_UPLOADS,
                    page: 1,
                    count: RESULT_COUNT,
                    autocorrect: false,
                    did_you_mean: false,
                    filter_by_user_level: false,
                },
            });

            const voiceoverRegistry = VoiceoverRegistry.getInstance();
            const representations = response.data.results
                .filter((result) => result.audio)
                .map((result) => {
                    const id = voiceoverRegistry.addItem(result);
                    return voiceoverRegistry.getRepresentation(id);
                });

            if (representations.length === 0) {
                return query
                    ? `No uploaded voiceovers found matching "${query}". Try omitting the query to list all the user's uploaded voiceovers.`
                    : `The user has no uploaded voiceovers.`;
            }

            return JSON.stringify(representations);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error searching for uploaded voiceovers: ${errorMessage}`;
        }
    }
}

const SearchUploadedVoiceoversResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
    toolCall,
}) => {
    if (isLoading) {
        return null;
    }
    let parsedResult;
    try {
        parsedResult = JSON.parse(result);
    } catch (error) {
        return <div>{result}</div>;
    }

    if (!Array.isArray(parsedResult)) {
        return <div>{result}</div>;
    }

    const representations = parsedResult || [];
    const args = toolCall ? JSON.parse(toolCall.function.arguments) : {};
    const query = args.query?.trim() || '';
    const voiceoverRegistry = VoiceoverRegistry.getInstance();

    return (
        <div>
            Found {representations.length} uploaded voiceover
            {representations.length !== 1 ? 's' : ''}
            {query ? ` matching "${query}"` : ''}
            <div>
                {representations.slice(0, 6).map((rep: VoiceoverRepresentation, index: number) => {
                    const mp3Url = voiceoverRegistry.getAudioAsset(rep.id)?.mp3_url;

                    return (
                        <div key={index} className="border p-l5-8 mb-l5-8 rounded">
                            <div className="fw-bold">{rep.title}</div>
                            <div className="text-muted small">
                                Duration: {Math.floor(rep.duration / 60)}:
                                {String(Math.floor(rep.duration % 60)).padStart(2, '0')}
                            </div>
                            {rep.tags && <div className="text-muted small">Tags: {rep.tags}</div>}
                            {mp3Url && (
                                <div>
                                    <audio
                                        controls
                                        preload="none"
                                        className="w-100"
                                        style={{ maxWidth: '300px', height: '30px' }}
                                    >
                                        <source src={mp3Url} type="audio/mpeg" />
                                        Your browser does not support the audio element.
                                    </audio>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {representations.length > 6 && (
                <div>... and {representations.length - 6} more voiceovers</div>
            )}
        </div>
    );
};
